import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz'
import { parseISO } from 'date-fns'

/**
 * Formats a UTC date/time string to the agency's timezone
 * @param utcDateString - ISO 8601 date string in UTC (e.g., "2024-12-13T10:30:00Z")
 * @param timezone - IANA timezone identifier (e.g., "America/New_York")
 * @param formatString - date-fns format string (default: 'PPpp' for full date and time)
 * @returns Formatted date string in the agency timezone
 */
export function formatToAgencyTimezone(
  utcDateString: string | Date,
  timezone: string,
  formatString: string = 'PPpp'
): string {
  try {
    const date = typeof utcDateString === 'string' ? parseISO(utcDateString) : utcDateString
    return formatInTimeZone(date, timezone, formatString)
  } catch (error) {
    console.error('Error formatting date to agency timezone:', error)
    return 'Invalid date'
  }
}

/**
 * Converts a UTC date to a Date object in the agency's timezone
 * @param utcDateString - ISO 8601 date string in UTC
 * @param timezone - IANA timezone identifier
 * @returns Date object adjusted to the agency timezone
 */
export function toAgencyTimezone(
  utcDateString: string | Date,
  timezone: string
): Date {
  try {
    const date = typeof utcDateString === 'string' ? parseISO(utcDateString) : utcDateString
    return toZonedTime(date, timezone)
  } catch (error) {
    console.error('Error converting to agency timezone:', error)
    return new Date()
  }
}

/**
 * Converts a date from the agency's timezone to UTC
 * @param localDate - Date object in the agency's timezone
 * @param timezone - IANA timezone identifier
 * @returns Date object in UTC
 */
export function fromAgencyTimezone(
  localDate: Date,
  timezone: string
): Date {
  try {
    return fromZonedTime(localDate, timezone)
  } catch (error) {
    console.error('Error converting from agency timezone:', error)
    return localDate
  }
}

/**
 * Formats a time duration in seconds to HH:MM:SS format
 * @param seconds - Duration in seconds
 * @returns Formatted duration string
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60

  return [hours, minutes, secs]
    .map((val) => val.toString().padStart(2, '0'))
    .join(':')
}

/**
 * Gets the current time in the agency's timezone
 * @param timezone - IANA timezone identifier
 * @param formatString - date-fns format string (default: 'PPpp')
 * @returns Formatted current time string
 */
export function getCurrentTimeInTimezone(
  timezone: string,
  formatString: string = 'PPpp'
): string {
  return formatInTimeZone(new Date(), timezone, formatString)
}

/**
 * Gets the timezone abbreviation (e.g., "EST", "PST", "IST")
 * @param timezone - IANA timezone identifier
 * @returns Timezone abbreviation
 */
export function getTimezoneAbbreviation(timezone: string): string {
  try {
    const date = new Date()
    const formatted = formatInTimeZone(date, timezone, 'zzz')
    return formatted
  } catch (error) {
    console.error('Error getting timezone abbreviation:', error)
    return timezone
  }
}

/**
 * Formats a date for time entry display (date + time)
 * @param utcDateString - ISO 8601 date string in UTC
 * @param timezone - IANA timezone identifier
 * @returns Formatted string like "Dec 13, 2024 10:30 AM"
 */
export function formatTimeEntryDateTime(
  utcDateString: string | Date,
  timezone: string
): string {
  return formatToAgencyTimezone(utcDateString, timezone, 'MMM d, yyyy h:mm a')
}

/**
 * Formats a date for calendar display (date only)
 * @param utcDateString - ISO 8601 date string in UTC
 * @param timezone - IANA timezone identifier
 * @returns Formatted string like "Dec 13, 2024"
 */
export function formatCalendarDate(
  utcDateString: string | Date,
  timezone: string
): string {
  return formatToAgencyTimezone(utcDateString, timezone, 'MMM d, yyyy')
}

/**
 * Formats time only (no date)
 * @param utcDateString - ISO 8601 date string in UTC
 * @param timezone - IANA timezone identifier
 * @returns Formatted string like "10:30 AM"
 */
export function formatTimeOnly(
  utcDateString: string | Date,
  timezone: string
): string {
  return formatToAgencyTimezone(utcDateString, timezone, 'h:mm a')
}

